import type { ReactElement } from 'react';
import React, { useCallback, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { toInteger } from 'lodash';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import type { z } from 'zod';
import Alert from 'atom-components/alert';
import LoadingErrorAlert from 'feature-components/loading-error-alert';
import { pluralise } from 'helpers/string.helpers';
import { countMessages, isAnActiveMarkerOfResponse } from 'pages/marking/helpers/marker.helpers';
import { markStatusDisplayName } from 'pages/marking/helpers/presentation.helpers';
import { refreshMarkingTables } from 'pages/marking/helpers/react-query.helpers';
import { marksOfResponseParams } from 'pages/marking/helpers/request-params.helpers';
import { MarkingPath } from 'pages/marking/routes';
import type { ChangeMarkerMark } from 'pages/marking/types';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import type { MeCustomerUser } from 'services/assist-api/entities/me/me';
import type { BaseTag } from 'services/assist-api/entities/tag/tags';
import {
  useDeleteTestInstanceResponseMarkItems,
  usePatchTestInstanceResponseMarkItems,
  usePostTestInstanceResponseMarkItems
} from 'services/assist-api/entities/test-instance-response-mark-item/hooks';
import type {
  PatchTestInstanceResponseMarkItem,
  PostTestInstanceResponseMarkItem
} from 'services/assist-api/entities/test-instance-response-mark-item/types';
import {
  usePatchTestInstanceResponseMark,
  usePostTestInstanceResponseMark,
  useTestInstanceResponseMarks
} from 'services/assist-api/entities/test-instance-response-mark/hooks';
import type {
  PatchTestInstanceResponseMark,
  PostTestInstanceResponseMark,
  TestInstanceResponseMarkWithItems
} from 'services/assist-api/entities/test-instance-response-mark/types';
import type { TestInstanceResponseMarkerWithCustomerUser } from 'services/assist-api/entities/test-instance-response-marker/types';
import type { ResponseItem } from 'services/assist-api/entities/test-instance-response/types';
import type { TestItemWithRubricItems } from 'services/assist-api/entities/test-item/test-items';
import type { TestRubricItemWithOptions } from 'services/assist-api/entities/test-rubric-item/types';
import { AssistQueryClient } from 'services/assist-api/query-client';
import { goBack } from 'utils/goBack';
import ConfirmFlagForAttentionModal, {
  shouldAskToFlagForAttention
} from './confirm-flag-for-attention-modal';
import ConfirmMarkCreationModal, {
  shouldCheckIfOtherMarkingHasOccurred
} from './confirm-mark-creation-modal';
import ConfirmMarkerChangeModal, {
  shouldAskToChangeActiveMarker
} from './confirm-marker-change-modal';
import ConfirmSubmitForReviewModal, {
  shouldAskToSubmitForReview
} from './confirm-submit-for-review-modal';
import MarkAdministration from './mark-administration';
import {
  loadDefaultValues,
  markFieldsToSave,
  markItemIdsToDelete,
  markItemsToPatch,
  markItemsToPost
} from './mark-response-form.helpers';
import {
  draftMarkResponseFormSchema,
  submitMarkResponseFormSchema
} from './mark-response-form.schema';
import type { MarkResponseFormValues } from './mark-response-form.schema';
import MarkSubmission from './mark-submission';
import QuestionCategories from './question-categories';
import type { UICategory } from './types';
import buildUICategories from './ui-categories-builder';

type Props = {
  responseId: number;
  rubricId: number;
  testItems: TestItemWithRubricItems[];
  allResponseItems: ResponseItem[];
  allRubricItems: TestRubricItemWithOptions[];
  attentionReasons: BaseTag[];
  canEditMark: boolean;
  canAdministrateMark: boolean;
  currentOrganisationUser: MeCustomerUser;
  currentUserMarker: TestInstanceResponseMarkerWithCustomerUser | undefined;
  activeMarker?: TestInstanceResponseMarkerWithCustomerUser;
  mark?: TestInstanceResponseMarkWithItems;
  markItemMarkers: TestInstanceResponseMarkerWithCustomerUser[];
  markItemMarkerOrganisationUsers: BaseCustomerUser[];
};

export default function MarkResponseForm({
  responseId,
  rubricId,
  testItems,
  allResponseItems,
  allRubricItems,
  attentionReasons,
  canEditMark,
  canAdministrateMark,
  currentOrganisationUser,
  currentUserMarker,
  activeMarker,
  mark,
  markItemMarkers,
  markItemMarkerOrganisationUsers
}: Props): ReactElement {
  enum Modal {
    ConfirmMarkerChange = 'confirmMarkerChange',
    ConfirmMarkCreation = 'confirmMarkCreation',
    ConfirmFlagForAttention = 'confirmFlagForAttention',
    ConfirmSubmitForReview = 'confirmSubmitForReview'
  }
  const [currentModal, setCurrentModal] = useState<Modal | undefined>(undefined);

  const categories: UICategory[] = buildUICategories(testItems, allResponseItems, allRubricItems);

  const defaultValues: MarkResponseFormValues = loadDefaultValues(
    categories,
    attentionReasons,
    currentUserMarker?.id,
    activeMarker?.id,
    mark
  );

  const draftResolver = zodResolver<z.ZodType<Partial<MarkResponseFormValues>>>(
    draftMarkResponseFormSchema
  );

  const submitResolver = zodResolver<z.ZodType<Partial<MarkResponseFormValues>>>(
    submitMarkResponseFormSchema
  );

  const methods = useForm({
    mode: 'onTouched',
    resolver: (...args) => {
      if (args[0].submitAction === 'saveDraftMarking') {
        return draftResolver(...args);
      }
      return submitResolver(...args);
    },
    defaultValues
  });
  const { handleSubmit, watch, setValue } = methods;

  const deleteTestInstanceResponseMarkItems = useDeleteTestInstanceResponseMarkItems({
    onSuccess: (data: PromiseRejectedResult[]) => {
      const rejected = data.filter((d) => d.status === 'rejected').length;
      const fulfilled = data.length - rejected;
      if (rejected) {
        toast.error(`Failed to unscore ${rejected} ${pluralise('mark item', rejected)}.`);
      }
      if (fulfilled) {
        toast.success(`Unscored ${fulfilled} ${pluralise('mark item', fulfilled)}.`);
      }
    },
    onError: (error) => {
      toast.error(`Failed to unscore mark items: ${error.message}`);
    }
  });

  const postTestInstanceResponseMarkItems = usePostTestInstanceResponseMarkItems({
    onSuccess: (data: PromiseRejectedResult[]) => {
      const rejected = data.filter((d) => d.status === 'rejected').length;
      const fulfilled = data.length - rejected;
      if (rejected) {
        toast.error(`Failed to score ${rejected} new ${pluralise('mark item', rejected)}.`);
      }
      if (fulfilled) {
        toast.success(`Scored ${fulfilled} new ${pluralise('mark item', fulfilled)}.`);
      }
    },
    onError: (error) => {
      toast.error(`Failed to score new mark items: ${error.message}`);
    }
  });

  const patchTestInstanceResponseMarkItems = usePatchTestInstanceResponseMarkItems({
    onSuccess: (data: PromiseRejectedResult[]) => {
      const rejected = data.filter((d) => d.status === 'rejected').length;
      const fulfilled = data.length - rejected;
      if (rejected) {
        toast.error(
          `Failed to update the scores of ${rejected} ${pluralise('mark item', rejected)}.`
        );
      }
      if (fulfilled) {
        toast.success(`Updated the scores of ${fulfilled} ${pluralise('mark item', fulfilled)}.`);
      }
    },
    onError: (error) => {
      toast.error(`Failed to update the scores of mark items: ${error.message}`);
    }
  });

  const patchTestInstanceResponseMark = usePatchTestInstanceResponseMark({
    onSuccess: ({ data }) => {
      toast.success(`Updated mark with status: ${markStatusDisplayName(data.json.status)}.`);
    },
    onError: (error) => {
      toast.error(`Failed to update mark: ${error.message}`);
    }
  });

  const postTestInstanceResponseMark = usePostTestInstanceResponseMark({
    onSuccess: ({ data }) => {
      toast.success(`Created mark with status: ${markStatusDisplayName(data.json.status)}.`);
    },
    onError: (error) => {
      toast.error(`Failed to create mark: ${error.message}`);
    }
  });

  const navigate = useNavigate();
  const redirectToMarkerDashboard = useCallback(() => {
    refreshMarkingTables();

    // Attempt to navigate the previous URL (so that we preserve Marker Dashboard filters). This
    // will also return the Marking Admin to the Marks to Review page if they arrived from there.
    goBack(
      () => navigate(-1),
      () => navigate(MarkingPath.MarkerDashboard)
    );
  }, [navigate]);

  /**
   * Updates a mark. For PATCH requests, the API can't handle mark items being saved as part of the
   * same request, so we need to send multiple requests and batch them together for saving the items
   * alongside the body of the mark.
   */
  const updateTestInstanceResponseMark = useCallback(
    async (formValues: MarkResponseFormValues) => {
      if (mark) {
        const markToPatch: PatchTestInstanceResponseMark = {
          id: mark.id,
          data: markFieldsToSave(responseId, rubricId, formValues, defaultValues)
        };
        const itemIdsToDelete: number[] = markItemIdsToDelete(formValues.markItems);
        const itemsToPatch: PatchTestInstanceResponseMarkItem[] = markItemsToPatch(
          formValues.markItems,
          mark
        );
        const itemsToPost: PostTestInstanceResponseMarkItem[] = markItemsToPost(
          formValues.markItems,
          mark.id
        );
        await patchTestInstanceResponseMark.mutateAsync(markToPatch);
        if (itemIdsToDelete.length > 0) {
          await deleteTestInstanceResponseMarkItems.mutateAsync(itemIdsToDelete);
        }
        if (itemsToPatch.length > 0) {
          await patchTestInstanceResponseMarkItems.mutateAsync(itemsToPatch);
        }
        if (itemsToPost.length > 0) {
          await postTestInstanceResponseMarkItems.mutateAsync(itemsToPost);
        }
        redirectToMarkerDashboard();
      }
    },
    [
      defaultValues,
      deleteTestInstanceResponseMarkItems,
      mark,
      patchTestInstanceResponseMark,
      patchTestInstanceResponseMarkItems,
      postTestInstanceResponseMarkItems,
      redirectToMarkerDashboard,
      responseId,
      rubricId
    ]
  );

  /**
   * Creates a mark, also creating any mark items that have been scored as part of the same network
   * request.
   */
  const createTestInstanceResponseMark = useCallback(
    async (formValues: MarkResponseFormValues) => {
      const testInstanceResponseMark: PostTestInstanceResponseMark = {
        ...markFieldsToSave(responseId, rubricId, formValues, defaultValues),
        testInstanceResponseMarkItems: markItemsToPost(formValues.markItems, undefined)
      };
      await postTestInstanceResponseMark.mutateAsync(testInstanceResponseMark);
      redirectToMarkerDashboard();
    },
    [defaultValues, postTestInstanceResponseMark, redirectToMarkerDashboard, responseId, rubricId]
  );

  /**
   * Creates or updates a mark and any mark items that have changed.
   */
  const saveTestInstanceResponseMark = useCallback(
    (formValues: MarkResponseFormValues) => {
      if (mark) {
        updateTestInstanceResponseMark(formValues);
      } else {
        createTestInstanceResponseMark(formValues);
      }
    },
    [createTestInstanceResponseMark, mark, updateTestInstanceResponseMark]
  );

  // Just before saving (after the Confirm Marker Change modal has been confirmed), this query can
  // refetch marks and mark items linked to the response to determine if the active marker has
  // marked anything.
  const watchedOtherMarkingKnown = watch('otherMarkingKnown');
  const marksQuery = useTestInstanceResponseMarks<ChangeMarkerMark>(
    marksOfResponseParams(responseId),
    { enabled: !watchedOtherMarkingKnown }
  );

  const watchedActiveMarkerId = watch('activeMarkerId');
  /**
   * When creating a new mark, check if the active marker is already marking any marks or mark items
   * of the response. In the event the active marker has already done marking against the response,
   * creating a new mark may be unintentional. The marker should be warned and given a choice to
   * abort saving.
   */
  const checkIfOtherMarkingHasOccurred = useCallback(async (): Promise<boolean> => {
    // Clear the cache to ensure that the marks of the response are refetched from the API
    // if the marker submits the form again:
    await AssistQueryClient.invalidateQueries(['test-instance-response-marks']);

    // Trigger the marks query to run again now that the cache has been cleared:
    const latestMarksQuery = await marksQuery.refetch();

    // Set the Other Marking Known flag to true so that when the modal is confirmed, this check
    // won't be triggered again.
    setValue('otherMarkingKnown', true);

    return isAnActiveMarkerOfResponse(
      toInteger(watchedActiveMarkerId),
      latestMarksQuery.data?.json || []
    );
  }, [marksQuery, setValue, watchedActiveMarkerId]);

  const onSubmit: SubmitHandler<MarkResponseFormValues> = useCallback(
    async (formValues): Promise<boolean> => {
      // Provide the option for the current user to set themselves as the active marker:
      if (shouldAskToChangeActiveMarker(canAdministrateMark, defaultValues, formValues)) {
        setCurrentModal(Modal.ConfirmMarkerChange);
        return false;
      }
      // Provide the option to the marker to flag for attention if attention reasons have been added
      // or the comment has changed (for either save action):
      if (shouldAskToFlagForAttention(canAdministrateMark, defaultValues, formValues)) {
        setCurrentModal(Modal.ConfirmFlagForAttention);
        return false;
      }
      // Provide the option to cancel if the marker is about to submit their mark for review:
      if (shouldAskToSubmitForReview(canAdministrateMark, formValues)) {
        setCurrentModal(Modal.ConfirmSubmitForReview);
        return false;
      }
      // Provide the option to cancel if the marker has already marked the response:
      if (shouldCheckIfOtherMarkingHasOccurred(mark, formValues)) {
        const otherMarkingHasOccurred = await checkIfOtherMarkingHasOccurred();
        if (otherMarkingHasOccurred) {
          setCurrentModal(Modal.ConfirmMarkCreation);
          return false;
        }
      }

      // When updating a mark, or we've already checked for other marking, it is safe to proceed
      // with saving straight away, rather than checking through other marking of the response:
      saveTestInstanceResponseMark(formValues);
      return true;
    },
    [
      defaultValues,
      canAdministrateMark,
      mark,
      saveTestInstanceResponseMark,
      Modal.ConfirmMarkerChange,
      Modal.ConfirmFlagForAttention,
      Modal.ConfirmSubmitForReview,
      Modal.ConfirmMarkCreation,
      checkIfOtherMarkingHasOccurred
    ]
  );

  const triggerFormSubmission = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const { isError } = marksQuery;
  if (isError) {
    return <LoadingErrorAlert queries={[marksQuery]} />;
  }

  const watchedCurrentUserMarkerId = watch('currentUserMarkerId');

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ConfirmMarkerChangeModal
          isOpen={currentModal === Modal.ConfirmMarkerChange}
          onClose={(): void => setCurrentModal(undefined)}
          onConfirm={(): void => {
            setCurrentModal(undefined);
            triggerFormSubmission();
          }}
          responseId={responseId}
          currentOrganisationUser={currentOrganisationUser}
        />
        <ConfirmFlagForAttentionModal
          isOpen={currentModal === Modal.ConfirmFlagForAttention}
          onClose={(): void => {
            setValue('hasAskedToFlagForAttention', true);
            setCurrentModal(undefined);

            // Continue saving if "No Review Needed" is chosen. This will allow the marker to be
            // able to submit the mark after changing the attention reasons or comment without it
            // being forced into review. It is the responsibility of the marker to confirm that
            // the mark should be flagged for attention if it does need to be reviewed.
            triggerFormSubmission();
          }}
          onConfirm={(): void => {
            setValue('hasAskedToFlagForAttention', true);
            setValue('flagForAttention', true);
            setCurrentModal(undefined);
            triggerFormSubmission();
          }}
          defaultValues={defaultValues}
          attentionReasons={attentionReasons}
        />
        <ConfirmSubmitForReviewModal
          isOpen={currentModal === Modal.ConfirmSubmitForReview}
          onClose={(): void => setCurrentModal(undefined)}
          onConfirm={(): void => {
            setValue('submitForReview', true);
            setCurrentModal(undefined);
            triggerFormSubmission();
          }}
        />
        <ConfirmMarkCreationModal
          isOpen={currentModal === Modal.ConfirmMarkCreation}
          onClose={(): void => {
            // When the marker chooses not to confirm the mark creation, setting the other marking
            // known status back to false will force the query to be refetched if the marker tries
            // to submit again. This helps in cases where there is a delay between submission
            // attempts and the active marker changes in the interm or marks further work in the
            // background:
            setValue('otherMarkingKnown', false);
            setCurrentModal(undefined);
          }}
          onConfirm={(): void => {
            setCurrentModal(undefined);
            triggerFormSubmission();
          }}
          responseId={responseId}
          marksOfResponse={marksQuery.data?.json || []}
        />
        {marksQuery.isSuccess &&
          isAnActiveMarkerOfResponse(
            toInteger(watchedActiveMarkerId),
            marksQuery.data?.json || []
          ) && (
            <Alert variant="warning">
              {watchedActiveMarkerId === watchedCurrentUserMarkerId
                ? 'You have'
                : 'The active marker has'}{' '}
              already marked{' '}
              {countMessages(toInteger(watchedActiveMarkerId), marksQuery.data?.json || [])} of this
              response (<b>#{responseId}</b>).
            </Alert>
          )}
        <QuestionCategories
          categories={categories}
          canEditMark={canEditMark}
          canAdministrateMark={canAdministrateMark}
          defaultValues={defaultValues}
          markItemMarkers={markItemMarkers}
          markItemMarkerOrganisationUsers={markItemMarkerOrganisationUsers}
        />
        <MarkAdministration canAdministrateMark={canAdministrateMark} />
        <MarkSubmission
          attentionReasons={attentionReasons}
          canEditMark={canEditMark}
          canAdministrateMark={canAdministrateMark}
          isSaving={
            postTestInstanceResponseMark.isLoading ||
            patchTestInstanceResponseMark.isLoading ||
            postTestInstanceResponseMarkItems.isLoading ||
            patchTestInstanceResponseMarkItems.isLoading ||
            deleteTestInstanceResponseMarkItems.isLoading
          }
        />
      </form>
    </FormProvider>
  );
}
